<template>
  <b-collapse
    v-model="isSearchOpen"
    class="general-search-bar-collapse"
  >
    <div class="search-bar-outer-wrapper">
      <div class="search-dropdown-menu-wrapper">
        <li
          class="search-bar-container"
          :class="{ 'has-error': errorMessage }"
          @click.stop
        >
          <div
            class="search-type-wrapper"
            @click.stop="toggleTypeSelector"
          >
            <span class="search-type-text">{{ searchTypeLabel }}</span>
            <feather-icon
              icon="ChevronDownIcon"
              size="16"
              class="chevron-icon"
              :class="{ 'chevron-rotated': isTypeSelectorOpen }"
            />

            <!-- Type Selector Dropdown Options -->
            <transition name="fade-slide">
              <div
                v-if="isTypeSelectorOpen"
                class="search-type-dropdown-options"
              >
                <div
                  class="dropdown-option"
                  :class="{ active: searchType === 'waybill' }"
                  @click.stop="selectSearchType('waybill')"
                >
                  Waybill
                </div>
                <div
                  class="dropdown-option"
                  :class="{ active: searchType === 'city' }"
                  @click.stop="selectSearchType('city')"
                >
                  City
                </div>
              </div>
            </transition>
          </div>

          <div class="vertical-divider" />

          <!-- Input Area -->
          <div class="search-input-wrapper">
            <input
              v-if="searchType === 'waybill'"
              key="waybill-input"
              ref="waybillInput"
              v-model="waybillNumber"
              type="text"
              placeholder="Enter Waybill Number"
              class="general-search-input"
              autocomplete="off"
              @keyup.enter="triggerSearch"
              @input="clearError"
            >
            <input
              v-else
              key="city-input"
              ref="cityInput"
              v-model="inputName"
              type="text"
              placeholder="Enter City Name"
              class="general-search-input"
              autocomplete="off"
              @keyup.enter="triggerSearch"
              @input="clearError"
            >
            <!-- Clear Button -->
            <feather-icon
              v-if="canSearch"
              icon="XIcon"
              size="16"
              class="clear-icon"
              @click.stop="clearInput"
            />
          </div>

          <!-- Action Button -->
          <button
            type="button"
            class="search-action-btn"
            :disabled="!canSearch"
            @click="triggerSearch"
          >
            <feather-icon
              icon="SearchIcon"
              size="20"
            />
          </button>
        </li>

        <!-- Error Warning Message -->
        <span
          v-if="errorMessage"
          class="mobile-error-text"
        >
          {{ errorMessage }}
        </span>
      </div>
    </div>

    <view-city-result-modal ref="viewCityResultModal" />
    <view-order-modal ref="viewOrderModal" />
  </b-collapse>
</template>

<script>
import { BCollapse } from 'bootstrap-vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import viewCityResultModal from '@/components/GeneralSearch/viewCityResultModal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const genarelSearchRepository = RepositoryFactory.get('genarelSearch')

export default {
  components: {
    BCollapse,
    ViewOrderModal,
    viewCityResultModal,
  },
  data() {
    return {
      searchType: 'waybill',
      isTypeSelectorOpen: false,
      selectedDemarcation: '',
      waybillNumber: '',
      disabled: true,
      orderId: '',
      inputName: '',
      errorMessage: '',
    }
  },
  computed: {
    isSearchOpen: {
      get() {
        return this.$store.state.app.isGeneralSearchOpen
      },
      set(val) {
        this.$store.commit('app/TOGGLE_GENERAL_SEARCH', val)
      },
    },
    searchTypeLabel() {
      return this.searchType === 'waybill' ? 'Waybill' : 'City'
    },
    canSearch() {
      const value = this.searchType === 'waybill' ? this.waybillNumber : this.inputName
      return !!(value && value.trim())
    },
  },
  watch: {
    isSearchOpen(val) {
      if (val) {
        this.clearFormAndValidation()
        this.$nextTick(() => {
          if (this.$refs.waybillInput) {
            this.$refs.waybillInput.focus()
          }
        })
      } else {
        this.isTypeSelectorOpen = false
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.closeTypeSelector)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeTypeSelector)
  },
  methods: {
    clearError() {
      this.errorMessage = ''
    },
    clearInput() {
      if (this.searchType === 'waybill') {
        this.waybillNumber = ''
        this.$nextTick(() => {
          if (this.$refs.waybillInput) this.$refs.waybillInput.focus()
        })
      } else {
        this.inputName = ''
        this.$nextTick(() => {
          if (this.$refs.cityInput) this.$refs.cityInput.focus()
        })
      }
      this.errorMessage = ''
    },
    toggleTypeSelector() {
      this.isTypeSelectorOpen = !this.isTypeSelectorOpen
    },
    closeTypeSelector() {
      this.isTypeSelectorOpen = false
    },
    selectSearchType(type) {
      this.searchType = type
      this.isTypeSelectorOpen = false
      this.errorMessage = ''
      if (type === 'city') {
        this.selectedDemarcation = 'City'
      } else {
        this.selectedDemarcation = ''
      }
      this.orderId = ''
      this.disabled = true
      this.$nextTick(() => {
        if (type === 'waybill' && this.$refs.waybillInput) {
          this.$refs.waybillInput.focus()
        } else if (type === 'city' && this.$refs.cityInput) {
          this.$refs.cityInput.focus()
        }
      })
    },
    async onClickSearchWayBill() {
      if (!this.waybillNumber) {
        this.errorMessage = 'Please enter waybill number'
        return
      }
      this.errorMessage = ''
      try {
        const { data } = (await genarelSearchRepository.getOrdersByWarehouse(this.waybillNumber)).data
        if (!data || !data.id) {
          this.errorMessage = 'Waybill not found'
          return
        }
        this.orderId = data.id
        this.onClickViewOrder()
      } catch (e) {
        this.disabled = false
        if (e.response && e.response.status === 404) {
          this.errorMessage = 'Waybill not found'
        } else if (e.response && e.response.data && e.response.data.message) {
          if (typeof e.response.data.message === 'string') {
            this.errorMessage = e.response.data.message
          } else {
            const msgs = Object.values(e.response.data.message)
            this.errorMessage = msgs.length ? msgs[0].error || msgs[0] : 'Error searching waybill'
          }
        } else if (e.response && e.response.data && e.response.data.errors) {
          const errors = Object.values(e.response.data.errors)
          this.errorMessage = errors.length ? errors[0][0] || errors[0] : 'Error searching waybill'
        } else {
          this.errorMessage = 'Waybill not found'
        }
      }
    },
    onClickViewOrder() {
      this.$refs.viewOrderModal.openModal(this.orderId)
      this.closeSearch()
    },
    async onClickSearchDemarcation() {
      if (!this.inputName) {
        this.errorMessage = 'Please enter city name'
        return
      }
      this.errorMessage = ''
      try {
        const { data } = (await genarelSearchRepository.cityListByName(this.inputName)).data
        if (!data || data.length === 0) {
          this.errorMessage = 'City not found'
          return
        }
        this.$refs.viewCityResultModal.openModal(this.inputName, data)
        this.closeSearch()
      } catch (e) {
        this.errorMessage = 'City not found'
      }
    },
    triggerSearch() {
      if (!this.canSearch) return
      if (this.searchType === 'waybill') {
        this.onClickSearchWayBill()
      } else {
        this.onClickSearchDemarcation()
      }
    },
    clearFormAndValidation() {
      this.waybillNumber = ''
      this.selectedDemarcation = ''
      this.inputName = ''
      this.disabled = true
      this.orderId = ''
      this.searchType = 'waybill'
      this.isTypeSelectorOpen = false
      this.errorMessage = ''
    },
    closeSearch() {
      this.isSearchOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/general-search.scss';
</style>
